import type { EbaCoverageGap, RateContext } from '../award-rate-lookup';
import type {
  CompiledConditionalPenaltyKind,
  CompiledConditionalPenaltyLine,
} from './eba-block-compiler';

export const EBA_REQUIRED_CONDITIONAL_PENALTY_COVERAGE_GAP_REASON =
  'EBA_REQUIRED_CONDITIONAL_PENALTY_MISSING';

type Employment = 'PERMANENT' | 'CASUAL';

interface ConditionalPenaltySource {
  conditionalPenalties?: readonly CompiledConditionalPenaltyLine[];
}

function canonicalEmployeeCategory(value: string | null | undefined): string | null {
  const normalized = value?.trim().toUpperCase() ?? '';
  if (!normalized) return null;
  if (normalized === 'DAYWORKER' || normalized === 'DAY_WORKER') return 'DAYWORKER';
  if (
    normalized === 'SHIFTWORKER' ||
    normalized === 'SHIFT_WORKER' ||
    normalized === 'ROTATING_ROSTER' ||
    normalized === 'PERMANENT_NIGHT' ||
    normalized === 'PERMANENT_NIGHT_SHIFT'
  ) {
    return 'SHIFTWORKER';
  }
  return normalized;
}

function lineMatchesEmployeeCategory(
  line: CompiledConditionalPenaltyLine,
  employeeCategory: string | null | undefined,
): boolean {
  if (employeeCategory === null || employeeCategory === undefined) return true;
  const configured = line.employeeCategories ?? [];
  if (configured.length === 0) return true;
  const category = canonicalEmployeeCategory(employeeCategory);
  return (
    category !== null &&
    configured.map((value) => canonicalEmployeeCategory(value)).includes(category)
  );
}

export function hhmmToMinutes(value: string): number | null {
  const match = value.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
}

export function localTimeHHMM(date: Date, timezone: string): string {
  return new Intl.DateTimeFormat('en-AU', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
    .format(date)
    .replace(/^24:/, '00:');
}

export function timeInWindow(value: string, window: readonly [string, string]): boolean {
  const current = hhmmToMinutes(value);
  const start = hhmmToMinutes(window[0]);
  const end = hhmmToMinutes(window[1]);
  if (current === null || start === null || end === null) return false;
  if (start <= end) return current >= start && current <= end;
  return current >= start || current <= end;
}

/**
 * SPAN test — does the worked interval [start,end] OVERLAP the window? Used for
 * General EA cl 44 ("works some or all of their hours between …"), unlike
 * `timeInWindow` which tests only the commencement time.
 *
 * Both the shift and the window may cross midnight. The shift end is treated as
 * next-day when `end <= start` (in HH:MM); the window end likewise. The window
 * is compared at −1440/0/+1440 offsets so an early-morning worked interval
 * (e.g. a shift running to 06:00) still overlaps a night window expressed as
 * 23:00→07:00. Boundary touches are exclusive (a shift ending exactly at the
 * window start, or starting exactly at the window end, does not overlap).
 */
export function shiftTouchesWindow(
  startHHMM: string,
  endHHMM: string,
  window: readonly [string, string],
): boolean {
  const s = hhmmToMinutes(startHHMM);
  let e = hhmmToMinutes(endHHMM);
  const w0 = hhmmToMinutes(window[0]);
  let w1 = hhmmToMinutes(window[1]);
  if (s === null || e === null || w0 === null || w1 === null) return false;
  if (e <= s) e += 1440; // shift crosses midnight (or 24h)
  if (w1 <= w0) w1 += 1440; // window crosses midnight
  for (const offset of [-1440, 0, 1440]) {
    const overlapStart = Math.max(s, w0 + offset);
    const overlapEnd = Math.min(e, w1 + offset);
    if (overlapStart < overlapEnd) return true;
  }
  return false;
}

/**
 * Does a line's `startWindow` match the shift, honouring its `windowMode`?
 * - COMMENCEMENT (default) → start time is inside the window.
 * - SPAN → the worked interval overlaps the window.
 * - FINISH → the finish time is inside the window.
 * SPAN and FINISH require the shift end time; if it is absent such a line cannot
 * match (fail-safe). The window mechanic is data-driven, so this one matcher
 * serves every provider's EA — no per-agreement code.
 */
function lineWindowMatchesShift(
  line: CompiledConditionalPenaltyLine,
  shiftStartTime: string,
  shiftEndTime: string | null,
): boolean {
  if (!line.startWindow) return true;
  if (line.windowMode === 'SPAN') {
    return (
      shiftEndTime != null && shiftTouchesWindow(shiftStartTime, shiftEndTime, line.startWindow)
    );
  }
  if (line.windowMode === 'FINISH') {
    return shiftEndTime != null && timeInWindow(shiftEndTime, line.startWindow);
  }
  return timeInWindow(shiftStartTime, line.startWindow);
}

interface FindConditionalPenaltyOptions {
  blockCode?: string | null;
  shiftStartTime?: string | null;
  shiftEndTime?: string | null;
  employeeCategory?: string | null;
}

function conditionalPenaltyMatches(
  source: ConditionalPenaltySource,
  kind: CompiledConditionalPenaltyKind,
  employment: Employment,
  options: FindConditionalPenaltyOptions,
): CompiledConditionalPenaltyLine[] {
  const candidates = (source.conditionalPenalties ?? []).filter(
    (line) =>
      line.kind === kind &&
      line.employmentType === employment &&
      (!options.blockCode || line.blockCode === options.blockCode) &&
      lineMatchesEmployeeCategory(line, options.employeeCategory),
  );
  if (!options.shiftStartTime) return candidates;
  return candidates.filter((line) =>
    lineWindowMatchesShift(line, options.shiftStartTime!, options.shiftEndTime ?? null),
  );
}

export function findConditionalPenaltyLine(
  source: ConditionalPenaltySource,
  kind: CompiledConditionalPenaltyKind,
  employment: Employment,
  options: FindConditionalPenaltyOptions = {},
): CompiledConditionalPenaltyLine | null {
  return conditionalPenaltyMatches(source, kind, employment, options)[0] ?? null;
}

/**
 * Resolve the single applicable shift-loading line for a shift, preferring the
 * HIGHEST multiplier when more than one window matches (e.g. a shift working
 * into both the afternoon and night spans takes the higher night loading — the
 * loadings substitute, they do not stack). Used by the category-scoped
 * (cl 44 / cl 6) path; the reducer mirrors this exact selection.
 */
export function findHighestConditionalPenaltyLine(
  source: ConditionalPenaltySource,
  kind: CompiledConditionalPenaltyKind,
  employment: Employment,
  options: FindConditionalPenaltyOptions = {},
): CompiledConditionalPenaltyLine | null {
  return conditionalPenaltyMatches(
    source,
    kind,
    employment,
    options,
  ).reduce<CompiledConditionalPenaltyLine | null>(
    (best, line) => (best === null || line.multiplier > best.multiplier ? line : best),
    null,
  );
}

/** The set of canonical employee categories the overlay's shift-loading lines are scoped to. */
function scopedShiftLoadingCategories(source: ConditionalPenaltySource): Set<string> {
  const set = new Set<string>();
  for (const line of source.conditionalPenalties ?? []) {
    if (line.kind !== 'SHIFT_START_WINDOW') continue;
    for (const value of line.employeeCategories ?? []) {
      const canonical = canonicalEmployeeCategory(value);
      if (canonical !== null) set.add(canonical);
    }
  }
  return set;
}

/**
 * Resolve the applicable category-scoped shift-loading line for a shift, and
 * decide whether the worker's category is CLASSIFIED. `unclassified` is true
 * when the category is null/empty OR does not canonicalise to a category the
 * overlay's lines are scoped to (e.g. `OTHER`, or a typo) — the caller then
 * fails closed (no loading + coverage gap). A RECOGNISED category that simply
 * has no window match (e.g. a day-worker on a day shift) is classified with a
 * null line (legitimately $0). Engine AND reducer call THIS, so the fail-closed
 * decision + line selection can never drift between the two oracles.
 */
export function resolveCategoryScopedShiftLoading(
  source: ConditionalPenaltySource,
  employment: Employment,
  options: {
    employeeCategory: string | null | undefined;
    shiftStartTime: string;
    shiftEndTime: string | null;
  },
): { line: CompiledConditionalPenaltyLine | null; unclassified: boolean } {
  const canonical = canonicalEmployeeCategory(options.employeeCategory);
  if (canonical === null || !scopedShiftLoadingCategories(source).has(canonical)) {
    return { line: null, unclassified: true };
  }
  const line = findHighestConditionalPenaltyLine(source, 'SHIFT_START_WINDOW', employment, {
    employeeCategory: options.employeeCategory,
    shiftStartTime: options.shiftStartTime,
    shiftEndTime: options.shiftEndTime,
  });
  return { line, unclassified: false };
}

export function conditionalPenaltyBlockCodeForKind(
  source: ConditionalPenaltySource,
  kind: CompiledConditionalPenaltyKind,
  blockCode?: string | null,
  employeeCategory?: string | null,
): string | null {
  if (!blockCode) return null;
  return (source.conditionalPenalties ?? []).some(
    (line) =>
      line.kind === kind &&
      line.blockCode === blockCode &&
      lineMatchesEmployeeCategory(line, employeeCategory),
  )
    ? blockCode
    : null;
}

export function pushMissingConditionalPenaltyGap(
  coverageGaps: EbaCoverageGap[],
  ctx: RateContext,
  kind: CompiledConditionalPenaltyKind,
  employment: Employment,
  blockCode?: string | null,
  employeeCategory?: string | null,
): void {
  const requiredCodes = ctx.requiredConditionalPenaltyCodes ?? [];
  const applicableLines = (ctx.conditionalPenalties ?? []).filter(
    (line) => line.kind === kind && lineMatchesEmployeeCategory(line, employeeCategory),
  );
  if (employeeCategory != null && applicableLines.length === 0) return;
  const knownRequired =
    blockCode != null
      ? requiredCodes.includes(blockCode) &&
        applicableLines.some((line) => line.blockCode === blockCode)
      : requiredCodes.some((code) => applicableLines.some((line) => line.blockCode === code));
  if (!knownRequired && !blockCode) return;

  const band = blockCode ?? kind;
  if (
    coverageGaps.some(
      (gap) =>
        gap.band === band &&
        gap.employment === employment &&
        gap.reason === EBA_REQUIRED_CONDITIONAL_PENALTY_COVERAGE_GAP_REASON,
    )
  ) {
    return;
  }
  coverageGaps.push({
    band,
    blockCode: blockCode ?? undefined,
    blockKind: 'PENALTY',
    employment,
    reason: EBA_REQUIRED_CONDITIONAL_PENALTY_COVERAGE_GAP_REASON,
  });
}

/** The daily-pair money contract is opt-in by compiled identity, never award alone. */
export function hasGeneralStaffDailyLoadedOvertime(source: {
  overtimeRules?: readonly import('./eba-block-compiler').CompiledOvertimeRule[];
  isEba?: boolean;
  awardCode?: string;
  baseAwardCode?: string | null;
}): boolean {
  if (
    source.awardCode !== undefined &&
    (!source.isEba || (source.baseAwardCode ?? source.awardCode) !== 'MA000018')
  )
    return false;
  return (source.overtimeRules ?? []).some(
    (rule) =>
      rule.sourceBlockCode === 'GA_DAILY_OT_FIRST_2' &&
      rule.shiftLoadingIsAdditive === true &&
      rule.overtimeOnLoadedRate === true,
  );
}

export interface ResolvedShiftLoading {
  line: CompiledConditionalPenaltyLine | null;
  premium: number;
  unclassified: boolean;
}

/**
 * One decision for ordinary loading and loaded OT in the GS daily-pair lane.
 * Use whole-shift envelope clocks for settlement fragments. SPAN deliberately
 * remains ANY overlap for FE 4 October (including the overtime tail).
 * Source identities and flat display bands do not override eligibility. Scoped
 * lines choose the highest eligible multiplier, including overlapping starts.
 * Legacy commencement lines retain the ordinary engine's shiftworker / 06–10
 * exclusion, then choose the highest matching line for BOTH components.
 */
export function resolveShiftLoading(
  source: ConditionalPenaltySource,
  employment: Employment,
  options: {
    employeeCategory?: string | null;
    shiftStartTime: string;
    shiftEndTime: string | null;
    dayType?: string;
  },
  casualLoadingRate = 0.25,
): ResolvedShiftLoading {
  if (options.dayType && options.dayType !== 'weekday')
    return { line: null, premium: 0, unclassified: false };
  const scoped = (source.conditionalPenalties ?? []).some(
    (line) =>
      line.kind === 'SHIFT_START_WINDOW' &&
      ((line.employeeCategories?.length ?? 0) > 0 || line.windowMode != null),
  );
  let line: CompiledConditionalPenaltyLine | null;
  if (scoped) {
    // An unscoped explicit windowMode can still use the time mechanic without
    // requiring a category taxonomy that the overlay has never declared.
    if (scopedShiftLoadingCategories(source).size > 0) {
      const resolved = resolveCategoryScopedShiftLoading(source, employment, {
        ...options,
        employeeCategory: options.employeeCategory,
      });
      if (resolved.unclassified) return { ...resolved, premium: 0 };
      line = resolved.line;
    } else {
      line = findHighestConditionalPenaltyLine(source, 'SHIFT_START_WINDOW', employment, options);
    }
  } else {
    const start = hhmmToMinutes(options.shiftStartTime);
    if (
      canonicalEmployeeCategory(options.employeeCategory ?? 'DAYWORKER') !== 'SHIFTWORKER' ||
      start === null ||
      (start >= 360 && start < 600)
    ) {
      return { line: null, premium: 0, unclassified: false };
    }
    line = findHighestConditionalPenaltyLine(source, 'SHIFT_START_WINDOW', employment, options);
  }
  return {
    line,
    premium: line
      ? Math.max(0, line.multiplier - (employment === 'CASUAL' ? 1 + casualLoadingRate : 1))
      : 0,
    unclassified: false,
  };
}
